import '@machobear/design-system/reset'
import '../../styles/globals.css'

import React, { FunctionComponent, useEffect } from 'react'
import type { AppLayoutProps, AppProps } from 'next/app'
import { useRouter } from 'next/router'
import { appWithTranslation } from 'next-i18next'
import { propEq } from 'ramda'
import { ApolloProvider } from '@apollo/client'
import { ThemeProvider, ToastProvider } from '@machobear/design-system'
import { lakshmi as theme } from '@machobear/design-system/themes/lakshmi'
import { QueryClientProvider } from '@tanstack/react-query'
import { CookiesProvider } from 'react-cookie'
import { ErrorBoundary } from 'react-error-boundary'
import { Provider } from 'react-redux'

import { flags, FlagsProvider } from '@/flags'
import client from '@/ports/api-client'
import { useApollo } from '@/lib/apollo'
import { PostHogProvider } from '@/lib/posthog'
import { themeProviderOptions } from '@/lib/themes'
import { AlertModalProvider } from '@/hooks/use-alert-modal-context'
import useCurrentSession from '@/hooks/use-current-session'
import { HeaderContextProvider } from '@/hooks/use-header-context'
import { useIOSViewport } from '@/hooks/use-ios-viewport'
import useLoadSession from '@/hooks/use-load-session'
import { useSetReferralData } from '@/hooks/use-referral-data'
import usePersistLocaleCookie from '@/hooks/usePersistLocaleCookie'
import ErrorPage from '@/features/ErrorPage'
import Brevo from '@/components/brevo'
import HotJar from '@/components/hotjar'
import {
  ImpressionProvider,
  type Impression
} from '@/components/ImpressionProvider/ImpressionProvider'
import { queryClient } from '@/components/query-client'
import { ToastContainer } from '@/components/toast-notifications'
import { UserPilot } from '@/components/userpilot'
import Viewport from '@/components/viewport'

import { useDispatch, useStore } from '../lib/redux'

import '../../devlink/global.css'

import { inter, openSans } from '@/lib/fonts'

import { DevLinkProvider } from '../../devlink'

function SessionLoader() {
  useLoadSession()
  const router = useRouter()
  const dispatch = useDispatch()
  useEffect(() => {
    const start = () => {
      dispatch.loading.setLoading()
    }
    const end = () => {
      dispatch.loading.stopLoading()
    }
    router.events.on('routeChangeStart', start)
    router.events.on('routeChangeComplete', end)
    router.events.on('routeChangeError', end)
    return () => {
      router.events.off('routeChangeStart', start)
      router.events.off('routeChangeComplete', end)
      router.events.off('routeChangeError', end)
    }
  }, [dispatch.loading, router.events])
  return null
}

function LanguageRedirect() {
  const router = useRouter()
  const { pathname, asPath, query, locale } = router
  const session = useCurrentSession()
  const sessionComplete =
    !!session?.id && propEq('registration_complete', '1', session)
  const userLanguage = session?.setting_language

  useEffect(() => {
    if (!sessionComplete || !userLanguage || userLanguage === locale) return
    router.push({ pathname, query }, asPath, { locale: userLanguage })
  }, [router, pathname, asPath, query, locale, userLanguage, sessionComplete])
  return null
}

const EmptyLayout: React.FC<{ children: JSX.Element }> = ({ children }) =>
  children

function syncImpressions(impressions: Impression[]) {
  const formData = new FormData()
  formData.append(
    'body',
    JSON.stringify(
      impressions.map(({ listingId, metadata }) => {
        return {
          listing_id: listingId,
          metadata: {
            is_sponsored: metadata.sponsored,
            page: metadata.page,
            section: metadata.section
          }
        }
      })
    )
  )
  return client.put('/l/imp', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

function MyApp({ Component, pageProps, universalCookies }: AppLayoutProps) {
  const apolloClient = useApollo(pageProps.initialApolloState)
  const store = useStore(pageProps.initialReduxState)

  usePersistLocaleCookie()
  useIOSViewport()
  useSetReferralData()
  const Layout = Component.Layout || EmptyLayout

  // Apply font variables to document root
  useEffect(() => {
    document.documentElement.classList.add(inter.variable, openSans.variable)
  }, [])

  return (
    <>
      <ErrorBoundary FallbackComponent={() => <ErrorPage />}>
        <Provider store={store}>
          <ApolloProvider client={apolloClient}>
            <QueryClientProvider client={queryClient}>
              <CookiesProvider cookies={universalCookies}>
                <FlagsProvider flags={flags}>
                  <PostHogProvider>
                    <DevLinkProvider>
                      <React.StrictMode>
                        <ThemeProvider
                          theme={theme}
                          options={themeProviderOptions}
                        >
                          <ToastProvider>
                            <AlertModalProvider>
                              <ImpressionProvider
                                syncInterval={5000}
                                onSync={syncImpressions}
                              >
                                <HotJar />
                                <Brevo />
                                <Viewport />
                                <SessionLoader />
                                <UserPilot />
                                <LanguageRedirect />
                                <HeaderContextProvider>
                                  <Layout>
                                    <Component {...pageProps} />
                                  </Layout>
                                </HeaderContextProvider>
                              </ImpressionProvider>
                              <ToastContainer />
                            </AlertModalProvider>
                          </ToastProvider>
                        </ThemeProvider>
                      </React.StrictMode>
                    </DevLinkProvider>
                  </PostHogProvider>
                </FlagsProvider>
              </CookiesProvider>
            </QueryClientProvider>
          </ApolloProvider>
        </Provider>
      </ErrorBoundary>
    </>
  )
}

export default appWithTranslation(MyApp as FunctionComponent<AppProps>)
